import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Store, Truck, ArrowLeft } from "lucide-react";
import AuthHeader from "@/components/AuthHeader";
import api, { formatApiError } from "@/lib/api";

export default function GoogleOnboarding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const gt = params.get("gt");
  const [session, setSession] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!gt) return setErr("Missing sign-in token. Please try again.");
    (async () => {
      try {
        const { data } = await api.get(`/auth/google/session/${encodeURIComponent(gt)}`);
        setSession(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || "Google sign-in expired. Please try again.");
      }
    })();
  }, [gt]);

  if (err) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <AuthHeader tag="ONBOARDING" />
        <div className="flex-1 flex items-center">
          <div className="mx-auto w-full max-w-md px-6 py-16 text-center">
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-3">[ROUTA · GOOGLE]</div>
            <h1 className="headline text-3xl text-[#0a0a0a] mb-3">Sign-in expired</h1>
            <p className="text-sm text-gray-500 mb-6">{err}</p>
            <Link to="/login" className="font-mono text-[11px] tracking-[0.14em] uppercase underline text-[#0a0a0a]">Back to sign-in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-sm text-gray-400 font-mono uppercase tracking-wide">Loading…</div>;
  }

  const first = (session.name || "").split(" ")[0] || "there";

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="google-onboarding">
      <AuthHeader tag="ONBOARDING" />
      <div className="flex-1 flex items-center">
        <div className="mx-auto w-full max-w-2xl px-6 py-16">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0a0a0a] mb-8">
            <ArrowLeft className="h-4 w-4" /> Cancel
          </Link>
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-4">[ROUTA · WELCOME]</div>
          <h1 className="headline text-4xl lg:text-5xl text-[#0a0a0a]">Welcome, {first}.</h1>
          <p className="mt-5 text-base text-gray-500 max-w-md">
            We couldn't find a Routa account for <span className="text-[#0a0a0a]">{session.email}</span>. Tell us how you'll use Routa to finish setting up your account.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <button
              data-testid="onboarding-pick-merchant"
              onClick={() => navigate(`/register?gt=${encodeURIComponent(gt)}`)}
              className="group text-left border border-gray-200 p-6 hover:border-[#0a0a0a] transition-colors bg-white"
            >
              <Store className="h-6 w-6 text-orange-500 mb-4" />
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-2">[01] Merchant</div>
              <div className="text-lg font-bold text-[#0a0a0a] mb-2">I ship deliveries</div>
              <p className="text-sm text-gray-500">Create a merchant account, pick a plan, and get approved to dispatch AI-optimized routes.</p>
              <div className="mt-5 font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 group-hover:underline">Continue as merchant →</div>
            </button>

            <button
              data-testid="onboarding-pick-driver"
              onClick={() => navigate(`/apply?gt=${encodeURIComponent(gt)}`)}
              className="group text-left border border-gray-200 p-6 hover:border-[#0a0a0a] transition-colors bg-white"
            >
              <Truck className="h-6 w-6 text-orange-500 mb-4" />
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400 mb-2">[02] Driver</div>
              <div className="text-lg font-bold text-[#0a0a0a] mb-2">I deliver for merchants</div>
              <p className="text-sm text-gray-500">Apply as an approved Routa operator. Licence + insurance + background check in one flow.</p>
              <div className="mt-5 font-mono text-[11px] tracking-[0.14em] uppercase text-orange-600 group-hover:underline">Continue as driver →</div>
            </button>
          </div>

          <p className="mt-10 text-xs text-gray-400">Signed in with {session.email}. This token expires in 15 minutes.</p>
        </div>
      </div>
    </div>
  );
}
